import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { db, auth } from '../firebaseConfig';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import Post from './Post';
import PostForm from './PostForm';
import AdUnit from './AdSense';
import { 
  FaList, 
  FaThLarge,
  FaCalendarAlt,
  FaTimes
} from 'react-icons/fa';
import Masonry from 'react-masonry-css';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';

// Configure the localizer
const localizer = momentLocalizer(moment);

const FlyingLights = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
    {[...Array(10)].map((_, i) => (
      <div
        key={i}
        className={`
          absolute rounded-full mix-blend-screen animate-flying-light
          ${i % 2 === 0 ? 'bg-purple-500' : 'bg-pink-500'}
        `}
        style={{
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          width: `${Math.random() * 50 + 10}px`,
          height: `${Math.random() * 50 + 10}px`,
          animationDuration: `${Math.random() * 20 + 10}s`,
          animationDelay: `${i * 2}s`,
          opacity: Math.random() * 0.5 + 0.3
        }}
      />
    ))}
  </div>
);

const FireAnimation = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
    {[...Array(5)].map((_, i) => (
      <div
        key={i}
        className={`
          absolute rounded-full animate-fire-pulse opacity-20
          ${i % 2 === 0 ? 'bg-orange-500' : 'bg-red-600'}
        `}
        style={{
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          width: `${Math.random() * 300 + 100}px`,
          height: `${Math.random() * 300 + 100}px`,
          filter: 'blur(100px)',
          animationDuration: `${Math.random() * 15 + 10}s`,
          animationDelay: `${i * 3}s`
        }}
      />
    ))}
  </div>
);

// Custom Event Component for Calendar
const CustomEvent = ({ event }) => {
  return (
    <div 
      className="rbc-event-content"
      style={{
        backgroundImage: `url(${event.resource.imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        width: '14%',
        height: '60%',
        position: 'absolute',
        top: 0,
        left: 0,
        opacity: 0.8
      }}
    >
      <div className="event-overlay">
        {event.title}
      </div>
    </div>
  );
};

const customStyles = `
  .my-masonry-grid {
    display: flex;
    width: auto;
    margin-left: -8px;
  }
  .my-masonry-grid_column {
    padding-left: 8px;
    background-clip: padding-box;
  }

  /* Calendar styles */
  .custom-calendar {
    background: transparent !important;
  }
  .custom-calendar .rbc-toolbar button {
    color: white !important;
  }
  .custom-calendar .rbc-toolbar-label {
    color: white !important;
  }
  .custom-calendar .rbc-header {
    color: white !important;
  }
  .custom-calendar .rbc-today {
    background-color: rgba(255, 255, 255, 0.1) !important;
  }
  .custom-calendar .rbc-off-range-bg {
    background-color: rgba(0, 0, 0, 0.2) !important;
  }
  .custom-calendar .rbc-date-cell {
    color: white !important;
  }
  .custom-calendar .rbc-button-link {
    color: white !important;
  }
`;

const Feed = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
    const [filteredPosts, setFilteredPosts] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [filterOptions, setFilterOptions] = useState({
        artStyle: '',
        dateRange: null
    });
    const [isCreditHelpOpen, setIsCreditHelpOpen] = useState(false);
    const [userCredits, setUserCredits] = useState(0);
    const [editingPost, setEditingPost] = useState(null);
    const [selectedPost, setSelectedPost] = useState(null);
    const navigate = useNavigate();

      // Masonry breakpoints
  const breakpointColumnsObj = {
    default: 4,
    1100: 3,
    700: 2,
    500: 1
  };

    useEffect(() => {
        const fetchUserCredits = async () => {
            try {
                const user = auth.currentUser;
                if (user) {
                    const userDoc = await getDoc(doc(db, 'profiles', user.uid));
                    if (userDoc.exists()) {
                        setUserCredits(userDoc.data().credits || 0);
                    }
                }
            } catch (error) {
                console.error('Error fetching user credits:', error);
            }
        };

        fetchUserCredits();
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const postsRef = collection(db, 'posts');
            let q = query(
                postsRef,
                where('visibility', '==', 'public'),
                orderBy('createdAt', 'desc'),
                limit(20)
            );

            // Apply art style filter if selected
            if (filterOptions.artStyle) {
                q = query(
                    q,
                    where('artStyle', '==', filterOptions.artStyle)
                );
            }

            const querySnapshot = await getDocs(q);
            const fetchedPosts = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setPosts(fetchedPosts);
        } catch (error) {
            console.error('Error fetching posts:', error);
            setError('Failed to load posts');
        } finally {
            setLoading(false);
        }
    };
    // Method to handle clicking on a post
  const handlePostClick = (post) => {
    // Navigate to individual post view
    navigate(`/dream/${post.id}`);
  };

    // Convert posts to calendar events
    const calendarEvents = posts.map(post => ({
        title: post.description || post.artStyleLabel,
        start: post.createdAt?.toDate() || new Date(),
        end: post.createdAt?.toDate() || new Date(),
        allDay: true,
        resource: post
    }));

    const handleEventClick = (event) => {
        setSelectedEvent(event.resource);
    };

    const toggleViewMode = () => {
        setViewMode(prevMode => prevMode === 'list' ? 'calendar' : 'list');
    };

    const handleNewPost = (newPost) => {
        setPosts(prevPosts => [newPost, ...prevPosts]);
    };

    const startEditingPost = (post) => {
        setEditingPost(post);
    };

    const artStyleOptions = [
        { value: '', label: 'All Styles' },
        { value: 'art-nouveau-generator', label: 'Art Nouveau' },
        { value: 'impressionist-style', label: 'Impressionist' },
        { value: 'cubist-art', label: 'Cubist' },
        { value: 'surrealist-dream', label: 'Surrealist' }
    ];

    const renderGridView = () => (
      <div>
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="my-masonry-grid"
          columnClassName="my-masonry-grid_column"
        >
          {posts.filter(post => post.imageUrl).map(post => (
            <div 
              key={post.id} 
              className="mb-2 cursor-pointer"
              onClick={() => setSelectedPost(post)}
            >
              <img 
                src={post.imageUrl} 
                alt="Dream visualization" 
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>
          ))}
        </Masonry>

        {/* Post Details Modal */}
        {selectedPost && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
            onClick={() => setSelectedPost(null)}
          >
            <div 
              className="bg-gray-900 rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4">
                <Post 
                  post={selectedPost} 
                  onEdit={startEditingPost}
                />
              </div>
              <button
                onClick={() => setSelectedPost(null)}
                className="absolute top-4 right-4 text-white hover:text-gray-300"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>
          </div>
        )}
      </div>
    );

    const renderViewToggle = () => (
      <div className="flex justify-center mb-4">
        <div className="flex bg-white/10 backdrop-blur-lg rounded-full p-1">
          <button 
            onClick={() => setViewMode('list')}
            className={`
              px-4 py-2 
              rounded-full 
              flex items-center 
              transition-colors 
              ${viewMode === 'list' ? 'bg-purple-500 text-white' : 'text-white/70 hover:bg-white/20'}
            `}
          >
            <FaList className="mr-2" /> List
          </button>
          <button 
            onClick={() => setViewMode('grid')}
            className={`
              px-4 py-2 
              rounded-full 
              flex items-center 
              transition-colors 
              ${viewMode === 'grid' ? 'bg-purple-500 text-white' : 'text-white/70 hover:bg-white/20'}
            `}
          >
            <FaThLarge className="mr-2" /> Grid
          </button>
          <button 
            onClick={() => setViewMode('calendar')}
            className={`
              px-4 py-2 
              rounded-full 
              flex items-center 
              transition-colors 
              ${viewMode === 'calendar' ? 'bg-purple-500 text-white' : 'text-white/70 hover:bg-white/20'}
            `}
          >
            <FaCalendarAlt className="mr-2" /> Calendar
          </button>
        </div>
      </div>
    );

    // Add the styles to the document
    useEffect(() => {
      const styleSheet = document.createElement("style");
      styleSheet.innerText = customStyles;
      document.head.appendChild(styleSheet);
      return () => {
        document.head.removeChild(styleSheet);
      };
    }, []);

    if (loading) return <div className="text-center p-4">Loading dreams...</div>;
    if (error) return <div className="text-center text-red-500 p-4">{error}</div>;

    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-900 via-purple-900 to-black text-white relative overflow-hidden">
        <div className="absolute top-4 right-4 z-50">
          {/* <NotificationBell /> */}
        </div>
        <FlyingLights />
        <FireAnimation />
        <div className="max-w-4xl mx-auto px-4 py-8 relative z-10">
          {/* Credits Indicator */}
          <div className="flex justify-center mb-2">
            <div className="bg-white/10 backdrop-blur-lg rounded-lg p-2 w-full max-w-md">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-semibold">Credits:</span>
                  <span className="text-sm font-bold text-purple-400">{userCredits}</span>
                </div>
                <button 
                  onClick={() => setIsCreditHelpOpen(!isCreditHelpOpen)}
                  className="text-xs text-purple-300 hover:text-purple-200 transition-colors"
                >
                  {isCreditHelpOpen ? 'Hide' : 'Learn More'}
                </button>
              </div>

              {isCreditHelpOpen && (
                <div className="text-xs text-gray-300 mt-1 space-y-1">
                  <p>Credits power your creative journey:</p>
                  <ul className="list-disc list-inside">
                    <li>Dream Journaling: Unlimited</li>
                    <li>AI Image Generation: 1 credit</li>
                    <li>Dream Interpretation: 1 credit</li>
                    <li>Earn credits by sharing dreams</li>
                  </ul>
                  <p className="text-[10px] text-gray-400 mt-1">
                    Tip: Engage more to earn more credits!
                  </p>
                </div>
              )}
            </div>
          </div>

          {renderViewToggle()}

          {viewMode === 'list' ? (
            <div className="space-y-6">
              <div className="mb-8">
                <PostForm 
                  onAddPost={handleNewPost} 
                  editingPost={editingPost}
                  startEditingPost={startEditingPost}
                />
              </div>
              
              {posts.map((post, index) => (
                <React.Fragment key={post.id}>
                  <div className="w-full">
                    <Post 
                      post={post} 
                      onEdit={startEditingPost}
                    />
                  </div>
                  {/* {index === 2 && <AdUnit adSlot="5780733927" adFormat="fluid" />} */}
                </React.Fragment>
              ))}
              {posts.length === 0 && (
                <div className="text-center p-8 bg-white/10 backdrop-blur-lg rounded-lg shadow">
                  <p className="text-gray-300">No dreams shared yet. Be the first to share!</p>
                </div>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            renderGridView()
          ) : (
            <div className="p-4 bg-white/10 backdrop-blur-lg rounded-lg">
              <Calendar
                localizer={localizer}
                events={calendarEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 600 }}
                onSelectEvent={handleEventClick}
                components={{
                  event: CustomEvent
                }}
                className="custom-calendar text-white"
              />
            </div>
          )}

          {/* Event Details Modal */}
          {selectedEvent && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 max-w-2xl w-full relative">
                <button 
                  onClick={() => setSelectedEvent(null)}
                  className="absolute top-4 right-4 text-white hover:text-gray-300"
                >
                  <FaTimes size={24} />
                </button>
                
                {selectedEvent.imageUrl && (
                  <img 
                    src={selectedEvent.imageUrl} 
                    alt={selectedEvent.description}
                    className="w-full h-96 object-cover rounded-lg mb-4"
                    onError={(e) => {
                      e.target.src = '/logo.png';
                    }}
                  />
                )}
                
                <h2 className="text-2xl font-bold mb-2 text-white">
                  {selectedEvent.artStyleLabel}
                </h2>
                
                <p className="text-gray-300 mb-4">
                  {selectedEvent.description}
                </p>
                
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-sm text-gray-400">By {selectedEvent.author}</span>
                  <span className="text-sm text-gray-400">
                    • {moment(selectedEvent.createdAt.toDate()).format('MMMM D, YYYY')}
                  </span>
                </div>
                
                <div className="flex space-x-2">
                  <span className="text-sm text-purple-300">
                    {selectedEvent.likes} Likes
                  </span>
                  <span className="text-sm text-purple-300">
                    {selectedEvent.comments?.length || 0} Comments
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
};

export default Feed;
